import { useEffect, useState, useCallback } from "react";
import { Zap, Users, Loader2, ShieldCheck, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { startSubscriptionCheckout, startPayPalCheckout, pollSubscriptionActive, currencySymbol } from "../lib/billing";
import { toast } from "sonner";

const STROKE = 1.5;

/**
 * Global paywall modal. Opens when any API call returns 402 upgrade_required
 * (dispatched as `hiretape:upgrade-required` by the axios interceptor), so the
 * recording / interview-creation flows stay untouched.
 */
export default function UpgradeModal() {
  const { user, member, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  const isAdmin = member?.role === "admin";

  useEffect(() => {
    const handler = (e) => {
      // Paywall enforcement may be parked (PAYWALL_ENFORCEMENT_ENABLED=false).
      // The backend then never returns 402 so this rarely fires, but we
      // double-suppress here: only open once we confirm enforcement is on.
      api
        .get("/billing/info")
        .then(({ data }) => {
          setInfo(data);
          if (data?.paywall_enforcement_enabled === false) return; // parked → never show
          setDetail(e.detail || null);
          setOpen(true);
        })
        .catch(() => {
          // Couldn't determine state — fall back to showing (safer for paid mode).
          setInfo(null);
          setDetail(e.detail || null);
          setOpen(true);
        });
    };
    window.addEventListener("hiretape:upgrade-required", handler);
    return () => window.removeEventListener("hiretape:upgrade-required", handler);
  }, []);

  const sym = currencySymbol(info?.currency || "USD");
  const configured = !!info?.razorpay_configured;
  const paypalConfigured = !!info?.paypal_configured;
  const anyConfigured = configured || paypalConfigured;

  const upgrade = useCallback(async (period) => {
    setBusy(period);
    try {
      const res = await startSubscriptionCheckout({ period, user });
      if (res.status === "submitted") {
        setBusy("verifying");
        toast.message("Payment submitted — confirming with Razorpay…");
        const active = await pollSubscriptionActive();
        if (active) {
          toast.success("You're on the Pro plan. You can record again.");
          refresh?.();
          setOpen(false);
        } else {
          toast.message("Payment received. Activation appears once Razorpay confirms.");
        }
      }
    } catch (err) {
      const code = err?.response?.data?.detail;
      toast.error(code === "billing_not_configured" ? "Billing isn't configured yet." : "Could not start checkout.");
    } finally {
      setBusy(null);
    }
  }, [user, refresh]);

  const paypalUpgrade = useCallback(async (period) => {
    setBusy(`paypal-${period}`);
    try {
      await startPayPalCheckout({ period });
      // Full-page redirect to PayPal follows (returns to /settings?paypal=return).
    } catch (err) {
      const code = err?.response?.data?.detail;
      toast.error(code === "billing_not_configured" ? "PayPal isn't configured yet." : "Could not start PayPal checkout.");
      setBusy(null);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
            <Zap className="h-5 w-5 text-emerald-700" strokeWidth={STROKE} />
          </div>
          <DialogTitle className="font-display text-xl">Upgrade to keep recording</DialogTitle>
          <DialogDescription className="text-[13px] leading-relaxed">
            {detail?.message ||
              `You've used all ${info?.free_interviews_total ?? 2} free interviews for this workspace. Upgrade to record more.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-600 flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-700" strokeWidth={STROKE} />
          Your existing interviews, transcripts and recordings stay fully accessible — billing never locks your audit records.
        </div>

        {!isAdmin ? (
          <p className="text-[13px] text-ink-500" data-testid="upgrade-need-admin">
            Ask a workspace admin to upgrade the plan.
          </p>
        ) : !anyConfigured ? (
          <p className="text-[13px] text-ink-500">Billing isn’t configured in this environment yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {configured && (
              <>
                <button onClick={() => upgrade("monthly")} disabled={!!busy} className="hp-btn-primary justify-center" data-testid="upgrade-monthly-btn">
                  {busy === "monthly" || busy === "verifying" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" strokeWidth={STROKE} />}
                  Monthly — {sym}{info?.price_per_seat_monthly}/seat/mo
                </button>
                <button onClick={() => upgrade("annual")} disabled={!!busy} className="hp-btn-outline justify-center" data-testid="upgrade-annual-btn">
                  {busy === "annual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" strokeWidth={STROKE} />}
                  Annual — {sym}{info?.price_per_seat_annual}/seat/yr (2 months free)
                </button>
              </>
            )}
            {paypalConfigured && (
              <>
                <button onClick={() => paypalUpgrade("monthly")} disabled={!!busy} className={`${configured ? "hp-btn-outline" : "hp-btn-primary"} justify-center`} data-testid="upgrade-paypal-monthly-btn">
                  {busy === "paypal-monthly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" strokeWidth={STROKE} />}
                  PayPal — {sym}{info?.price_per_seat_monthly}/seat/mo
                </button>
                <button onClick={() => paypalUpgrade("annual")} disabled={!!busy} className="hp-btn-outline justify-center" data-testid="upgrade-paypal-annual-btn">
                  {busy === "paypal-annual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" strokeWidth={STROKE} />}
                  PayPal annual — {sym}{info?.price_per_seat_annual}/seat/yr
                </button>
              </>
            )}
            {busy === "verifying" && (
              <p className="text-center text-[12px] text-ink-400" data-testid="upgrade-verifying">Confirming your payment…</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
